import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Cross } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/content";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/90 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-white">
            <Cross className="h-4 w-4" />
          </span>
          <span className="font-serif text-xl tracking-tight text-ink md:text-2xl">
            GetWell<span className="italic text-brand">Soon</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${slug(l.label)}`}
              className={({ isActive }) =>
                `relative text-sm font-semibold transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:bg-brand after:transition-all after:duration-300 ${
                  isActive
                    ? "text-brand after:w-full"
                    : "text-ink/70 after:w-0 hover:text-brand hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            asChild
            className="h-11 rounded-full bg-brand px-6 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
          >
            <Link to="/contact" data-testid="nav-cta-button">
              Speak with a Care Coordinator
            </Link>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-ink lg:hidden"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] max-w-sm border-border bg-white p-0">
            <SheetHeader className="border-b border-border p-6 text-left">
              <SheetTitle className="font-serif text-xl tracking-tight text-ink">
                GetWell<span className="italic text-brand">Soon</span>
              </SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col p-6" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${slug(l.label)}`}
                  className={({ isActive }) =>
                    `border-b border-border py-4 font-serif text-lg transition-colors ${
                      isActive ? "text-brand" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Button
                asChild
                className="mt-8 h-12 rounded-full bg-brand font-semibold text-white hover:bg-brand-dark"
              >
                <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-nav-cta-button">
                  Speak with a Care Coordinator
                </Link>
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
